<template>
  <div v-if="!loading">
    <WdsAlert v-if="errorBannerMessage" error class="m-b-15">{{ errorBannerMessage }}</WdsAlert>

    <FlowHeading
      :title="$t('Profile')"
      title-type="title1"
      :sub-title="
        $t(
          'Fill out your profile to be eligible for more job offers from :company in the future.',
          {
            company: invitedBy?.company || 'n/a',
          },
        )
      "
    />

    <WdsStack direction-column gap-md>
      <WdsAlert
        info
        closable
        :hidden="alertInfo == ''"
        :description="alertInfo"
        @click="alertInfo = ''"
      >
        <WdsStack direction-row gap-md>
          <WdsIcon name="idea"></WdsIcon>
          <WdsText class="tw-text-indigo-500">{{ $t('Showcase your expertise!') }}</WdsText>
        </WdsStack>
      </WdsAlert>
      <WdsInput
        v-model="jobTitle"
        :disabled="isSubmitting"
        :label="$t('Role')"
        :placeholder="$t('e.g. UX Designer')"
        :error="errors['jobTitle']"
        lg
        required
      />

      <WdsStack direction-column gap-sm>
        <WdsText :text="$t('Skills')" />

        <TypeSelect
          name="skills"
          tag-type="skills"
          :placeholder="$t('Add skills')"
          :selected-in="skills"
          :show-typesuggestions="false"
          :can-add-unsupported-types="true"
        />
      </WdsStack>

      <WdsStack direction-column gap-sm>
        <WdsText :text="$t('Links')" />
        <div class="f-12 c-gray">
          {{ $t('e.g Linkedin profile, portfolio website') }}
        </div>

        <div v-for="(link, index) in links" :key="link.key">
          <WdsStack align-start gap-sm>
            <div class="tw-grow">
              <WdsInput
                v-model="links[index].url"
                :disabled="isSubmitting"
                :error="errors?.[`links.${index}`]"
                prefix-icon="link"
              />
            </div>

            <WdsButtonGhost class="!tw-mt-2 tw-pt" @click="removeLink(index)">
              <WdsIcon name="close" size="16" class="tw-text-gray-600" />
            </WdsButtonGhost>
          </WdsStack>
        </div>

        <div>
          <WdsButtonGhost primary :disabled="isSubmitting" @click="addLink('')">
            <WdsStack align-center gap-sm>
              <WdsIcon name="plus" size="18" />
              {{ !links.length ? $t('Add link') : $t('Add another link') }}
            </WdsStack>
          </WdsButtonGhost>
        </div>
      </WdsStack>
    </WdsStack>

    <div class="tw-mt-6">
      <WdsButton full-width lg primary :disabled="!canSubmit" @click="update">
        <div class="tw-flex">
          <transition name="fade">
            <ButtonSpinner v-if="isSubmitting" size="sm" color="blue" />
          </transition>
          {{ $t('Continue') }}
        </div>
      </WdsButton>
    </div>
  </div>
</template>

<script setup>
import gql from 'graphql-tag'
import { computed, ref, inject } from 'vue'
import {
  WdsAlert,
  WdsStack,
  WdsButtonGhost,
  WdsInput,
  WdsButton,
  WdsText,
  WdsIcon,
} from '@worksome/wds-components'

import { publicApi } from '@/app-bootstrapping/SetUpApollo'
import { UpdateWorkerMutation } from '@/components/GraphQL/mutations/Workers/UpdateWorkerMutation'
import TypeSelect from '@/components/TypeSelect.vue'
import ButtonSpinner from '@/components/ButtonSpinner.vue'
import FlowHeading from '@/components/flows/FlowHeading.vue'
import { FormValidation } from '@/utils/formValidation'
import { isEmpty } from '@/utils/tools'

defineProps({
  invitedBy: {
    type: Object,
    required: true,
  },
})

const next = inject('next')

const loading = ref(true)
const isSubmitting = ref(false)

const errors = ref({})
const errorBannerMessage = ref(null)

const jobTitle = ref('')
const skills = ref([])
const links = ref([])

const alertInfo = ref(
  $t(
    'Highlighting your experience makes your profile more attractive and creates more hiring opportunities',
  ),
)

const lacksRequiredFields = computed(() => !!(isEmpty(jobTitle.value) || isEmpty(skills.value)))

const canSubmit = computed(() => !isSubmitting.value && !lacksRequiredFields.value)

const loadFreelancer = async () => {
  loading.value = true

  await publicApi
    .query({
      fetchPolicy: 'no-cache',
      query: gql`
        query Worker($id: ID!) {
          worker(id: $id) {
            jobTitle
            skills {
              name
            }
            profile {
              links {
                url
              }
            }
          }
        }
      `,
      variables: {
        id: currentAccount.globalId,
      },
    })
    .then((result) => {
      const info = result.data.worker

      jobTitle.value = info.jobTitle
      skills.value = info.skills.map((skill) => skill.name)
      links.value = (info.profile?.links ?? []).map((link, index) => ({
        key: index,
        url: link.url,
      }))

      loading.value = false
    })
}

const resetErrors = () => {
  errors.value = {}
  errorBannerMessage.value = null
}

const addLink = (url) => {
  const hasEmptyRows = links.value.some((link) => link.url === '')

  if (!hasEmptyRows) {
    links.value = [...links.value, { key: links.value.length, url }]
  }
}

const removeLink = (linkIndex) => {
  links.value.splice(linkIndex, 1)

  delete errors.value?.[`links.${linkIndex}`]
}

const update = async () => {
  isSubmitting.value = true

  await publicApi
    .mutate({
      mutation: UpdateWorkerMutation,
      variables: {
        input: {
          id: currentAccount.globalId,
          jobTitle: jobTitle.value,
          skills: skills.value,
          links: links.value.map((link) => link.url),
        },
      },
    })
    .then(() => {
      resetErrors()

      next()
    })
    .catch((error) => {
      resetErrors()

      const { graphQLErrors } = error

      if (FormValidation.containsFormValidations(graphQLErrors)) {
        errors.value = FormValidation.getSingularGraphqlErrors(graphQLErrors, 'input.')
      } else if (FormValidation.containsErrorMessage(graphQLErrors)) {
        errorBannerMessage.value = graphQLErrors[0].message
      } else {
        errorBannerMessage.value = $t('An error occurred')
      }

      isSubmitting.value = false
    })
}

loadFreelancer()
</script>

<style scoped>
:deep(label) {
  @apply tw-mb-0;
}
.wds-input-group {
  margin: 24px 0;
}
.wds-input-group > .wds-input-wrapper > .wds-input-label {
  margin-bottom: unset;
}
:deep(.wds-select-wrapper .wds-select-button-display) {
  padding: 0.5625rem 0.75rem;
}
</style>
